<template>
  <div class="kpt-detail-content-page" v-if="group">
    <vue-headful :title="`${group.name} - Overview`" />
    <div class="kpt-detail-content-actions">
      <div class="buttons" v-if="!isConfirmDelete && !isEditing">
        <b-button type="is-primary is-outlined" icon-left="sync-alt" size="is-small" :disabled="isRefreshing" @click="refreshGroup">Refresh</b-button>
        <b-button type="is-primary is-outlined" icon-left="edit" size="is-small" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')" :disabled="isEditing" @click="editGroup">Edit</b-button>
        <b-button type="is-danger is-outlined" icon-left="trash-alt" size="is-small" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')" :disabled="isConfirmDelete || isDeleting" @click="isConfirmDelete = true">Delete</b-button>
      </div>
      <div class="buttons" v-if="isConfirmDelete">
        <b-button type="is-danger is-outlined" size="is-small" icon-left="trash-alt" :loading="isDeleting" @click="deleteGroup">Delete</b-button>
        <b-button type="is-primary is-outlined" size="is-small" icon-left="times" :disabled="isDeleting" @click="isConfirmDelete = false">Cancel</b-button>
      </div>
      <div class="buttons" v-if="isEditing">
        <b-button type="is-primary is-outlined" size="is-small" icon-left="edit" :loading="isUpdating" @click="updateGroup" :disabled="!canSave">Save</b-button>
        <b-button type="is-primary is-outlined" size="is-small" icon-left="times" :disabled="isUpdating" @click="resetEdit">Cancel</b-button>
      </div>
    </div>
    <div v-if="isConfirmDelete" class="my-2">
      <b-message type="is-warning">
        <h6>Are you sure you want to delete this group: <i>{{group.name}}</i>?</h6>
      </b-message>
    </div>
    <header>
      <h6 class="is-uppercase"><strong>Details</strong></h6>
    </header>
    <div v-if="isEditing">
      <b-field label="Name" horizontal required left>
        <b-input type="text" spellcheck="false" autocomplete="off" placeholder="Group Name" expanded v-model="groupEdit.name" @input="assessChanges" required></b-input>
      </b-field>
      <b-field label="Group Owner" horizontal left required>
        <b-select v-model="groupEdit.ownerId" @input="groupOwnerSelected" required>
          <option v-if="accounts.length == 1" :value="user.accountId">{{user.accountName}}</option>
          <option
            v-else
            v-for="option in accounts"
            :value="option.id"
            :key="option.id">
            {{ option.name }}
          </option>
        </b-select>
      </b-field>
      <b-field label="Entity Type" horizontal required left>
        <b-select v-model="groupEdit.entityType" placeholder="Select an entity type" @input="resetEntities" expanded required>
          <option
            v-for="option in groupEntityTypes"
            :value="option.name"
            :key="option.name">
            {{ option.displayName }}
          </option>
        </b-select>
      </b-field>
      <b-field label="Share With" horizontal left required>
        <account-selector ref="shareWithAccountSelect" 
          @account-selected="sharedAccountSelected" 
          :can-select="canSelectAccount" 
          :pre-selected="groupEdit.sharedWith" 
          :exclude-accounts="groupEdit.ownerId ? [groupEdit.ownerId] : []"></account-selector>
      </b-field>
      <div class="kpt-email-share-wrapper">
        <b-message type="is-info" class="mt-4">
          OR enter an email address and we will attempt to match the address with an existing Account. If no match is found, the recipient will be invited to establish an account with the Kymeta Access Portal via email.
        </b-message>
        <b-field label="Email Address" 
          :type="{'is-danger' : validationErrors.includes('inviteEmailAddress')}" 
          :message="{'This email is invalid.' : validationErrors.includes('inviteEmailAddress')}">
          <b-input 
            type="email" 
            placeholder="someone@kymetacorp.com" 
            v-model="groupEdit.inviteEmailAddress" 
            @input="emailInputReceived" 
            :disabled="!!groupEdit.sharedWith.length" expanded></b-input>
        </b-field>
      </div>
    </div>
    <div v-else>
      <detail-field>
        <template v-slot:label>Name</template>
        <template v-slot:value>{{ group.name || '-' }}</template>
      </detail-field>
      <detail-field>
        <template v-slot:label>Owner</template>
        <template v-slot:value>{{ getAccountNameFromId(group.ownerId) }}</template>
      </detail-field>
      <detail-field>
        <template v-slot:label>Shared With</template>
        <template v-slot:value>{{ sharedWith }}</template>
      </detail-field>
      <detail-field>
        <template v-slot:label>Entity Type</template>
        <template v-slot:value><span class="is-capitalized">{{ group.entityType || '-' }}</span></template>
      </detail-field>
      <b-message type="is-info" class="mt-4" v-if="hasPendingInvite">
        You have elected to share this group with an email address. Kymeta has sent an email to <span class="has-text-weight-bold">'{{ sharedWith }}'</span>, inviting them to the Kymeta Access Portal.
        Once the individual has successfully logged in Kymeta will associate their Account with this group.
      </b-message>
    </div>
    <hr>
    <header class="mb-1">
      <h6 class="is-uppercase"><strong>Entities</strong></h6>
    </header>
    <entity-selector v-if="isEditing" ref="entitySelector" 
      :group-entity-type="groupEdit.entityType" 
      @entity-selected="entitySelected" 
      :pre-selected="groupEdit.entities"
      :group-owner="groupEdit.ownerId"></entity-selector>
    <group-entities v-else :entities="groupEntities" :group-entity-type="group.entityType"></group-entities>
  </div>
</template>
<script>
import { orderBy, isEqual, uniqBy } from 'lodash';
import AccountsSelectorComponent from '../../accounts/components/AccountsSelectorComponent.vue';
import GroupEntitySelectorComponent from '../components/GroupEntitySelectorComponent.vue';
import GroupEntitiesComponent from '../components/GroupEntitiesComponent.vue';

export default {
  data() {
    return {
      isRefreshing: false,
      isDeleting: false,
      isConfirmDelete: false,
      isEditing: false,
      isUpdating: false,
      canSave: false,
      canSelectAccount: false,
      groupEdit: {
        name: null,
        ownerId: null,
        entityType: null,
        inviteEmailAddress: null,
        entities: [],
        sharedWith: []
      },
      validationErrors: [],
      currentPage: 1,
      pageSize: 50,
      sortBy: null,
      sortDirection: 'asc'
    }
  },
  components: { 
    'account-selector': AccountsSelectorComponent,
    'entity-selector': GroupEntitySelectorComponent,
    'group-entities': GroupEntitiesComponent
  },
  computed: {
    group() { return this.$store.state.groups.group; },
    groupEntities() {
      let entities = [];
      if (!this.group) return entities;

      // based on entity group type we extract the type of entities we need
      switch (this.group.entityType) {
        case 'remote':
          entities = this.group.remotes || [];
          break;
        case 'device':
          entities = this.group.devices || [];
          break;
        case 'subscription':
          entities = this.group.subscriptions || [];
          break;
        default:
          entities = [];
          break;
      }

      return entities;
    },
    sharedWith() {
      if (!this.group) return '-';
      // check for any pending invites on the group
      if (this.hasPendingInvite) {
        const invite = this.group.invites[0];
        // email is present, so display the email that was invited
        return invite?.email || '-';
      }
      // if there is no pending invite, we (should) have a sharedWith account defined
      return this.group.sharedWith[0] ? this.group.sharedWith[0].name : '-';
    },
    hasPendingInvite() { return this.group.invites && !!this.group.invites.length; },
    accounts() { 
      return orderBy(this.$store.state.accounts.accounts, ['name'], ['asc']);
    },
    sharedAccounts() {
      return this.$store.state.accounts.sharedAccounts;
    },
    allAccounts() {
      // join accounts and sharedAccounts arrays
      const concatAccounts = this.accounts.concat(this.sharedAccounts);
      // return unique to prevent duplicates
      return uniqBy(concatAccounts, 'id');
    },
    groupEntityTypes() {
      return [
        { name: 'remote', displayName: 'Remote' },
        { name: 'device', displayName: 'Device' },
        { name: 'subscription', displayName: 'Subscription' }
      ]
    }
  },
  methods: {
    async refreshGroup() {
      // KyAnalytics client plugin, event tracker
      this.$kya.event('groups', 'refreshGroup', 'Refresh Group', { groupId: this.group.id });
      await this.$store.dispatch('fetchGroup', this.group.id);
    },
    async deleteGroup() {
      // KyAnalytics client plugin, event tracker
      this.$kya.event('groups', 'deleteGroup', 'Delete Group clicked', { groupId: this.group.id });

      this.isDeleting = true;
      const deleteResult = await this.$store.dispatch('deleteGroup', this.group);
      this.isDeleting = false;
      if (!deleteResult.isSuccess) {
        // inform user of error
        this.$buefy.toast.open({
          duration: 3000,
          message: deleteResult.err?.message || 'Error encountered when attempting to delete group.',
          type: 'is-danger'
        });
        return;
      }

      // iform user of success
      this.$buefy.toast.open({
        duration: 3000,
        message: 'Group successfully deleted.',
        type: 'is-success'
      });

      // group was delete successfully, route the user to the group overview detail page
      this.$router.push({ name: 'Groups' });
    },
    editGroup() {
      this.resetEdit();
      this.isEditing = true;
      this.groupEdit = { ...this.group };
      this.groupEdit.sharedWith = [...this.group.sharedWith];
      this.groupEdit.entities = [...this.groupEntities];

      this.canSelectAccount = this.groupEdit.sharedWith && !!this.groupEdit.sharedWith.length;

      // check to see if there are any pending invites
      if (this.group.invites && !!this.group.invites.length) {
        const invite = this.group.invites[0];
        this.groupEdit.inviteEmailAddress = invite?.email;
      }
      // KyAnalytics client plugin, event tracker
      this.$kya.event('groups', 'editGroup', 'Edit Group clicked', { groupId: this.group.id });
    },
    resetEdit() {
      this.isEditing = false;      
      this.groupEdit = {
        name: null,
        ownerId: null,
        entityType: null,
        inviteEmailAddress: null,
        entities: [],
        sharedWith: []
      };
      this.assessChanges();
      // KyAnalytics client plugin, event tracker
      this.$kya.event('groups', 'resetGroupEdit', 'Reset Group Edit clicked', { groupId: this.group.id });
    },
    assessChanges() { // wrote this as a method instead of a computed because the computed prop was not playing nicely with my entities array. shame.
      // reset default
      this.canSave = false;
      this.validationErrors = [];

      if (!this.groupEdit.name?.trim()) { this.canSave = false; return; }
      // validate the email address when it is present
      if (this.groupEdit.inviteEmailAddress && !this.testEmailAddressValidity(this.groupEdit.inviteEmailAddress)) { 
        this.validationErrors.push('inviteEmailAddress');
        this.canSave = false; 
        return; 
      }

      if (this.group.name != this.groupEdit.name) this.canSave = true;
      if (this.group.entityType != this.groupEdit.entityType) this.canSave = true;
      if (this.group.ownerId != this.groupEdit.ownerId) this.canSave = true;

      // compare group entities
      if (!isEqual(this.groupEntities, this.groupEdit.entities)) this.canSave = true;

      // compare sharing
      if (this.groupEdit.sharedWith && !!this.groupEdit.sharedWith.length && !isEqual(this.group.sharedWith, this.groupEdit.sharedWith)) this.canSave = true;
      if (this.groupEdit.inviteEmailAddress && this.group.inviteEmailAddress != this.groupEdit.inviteEmailAddress) this.canSave = true;
    },
    groupOwnerSelected() {
      this.resetEntities();
      // refresh list of accounts to ensure group owner is not available in shared with selection
      this.$refs.shareWithAccountSelect.refreshAccounts();

      if (this.groupEdit.entityType) {
        // refresh the list of available entities based on selected owner
        this.$refs.entitySelector.refreshEntities();
      }
      this.assessChanges();
    },
    resetEntities() {
      this.groupEdit.entities = [];
      this.$refs.entitySelector.selectedEntities = [];

      // check for edits
      this.assessChanges();
    },
    testEmailAddressValidity(emailAddress) {
      const re = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      return re.test(emailAddress);
    },
    entitySelected(entities) {
      this.groupEdit.entities = [...entities];
      // check for edits
      this.assessChanges();
    },
    async updateGroup() {
      this.isUpdating = true;
      // gather payload - nulls will be ignored
      const payload = {
        id: this.group.id,
        name: this.group.name != this.groupEdit.name 
          ? this.groupEdit.name
          : null,
        ownerId: this.group.ownerId != this.groupEdit.ownerId
          ? this.groupEdit.ownerId
          : null,
        entityType: this.groupEdit.entityType != this.group.entityType 
          ? this.groupEdit.entityType 
          : null,
        entities: this.groupEdit.entities.map(e => e.id),
        sharedWith: this.groupEdit.sharedWith && !!this.groupEdit.sharedWith.length
          ? this.groupEdit.sharedWith.map(sw => sw.id)
          : null,
        inviteEmailAddress: this.group.inviteEmailAddress != this.groupEdit.inviteEmailAddress
          ? this.groupEdit.inviteEmailAddress
          : null
      }
      // send the request
      const updateResult = await this.$store.dispatch('updateGroup', payload);
      this.isUpdating = false;
      if (!updateResult.isSuccess) {
        // inform user of error
        this.$buefy.toast.open({
          duration: 3000,
          message: updateResult.err?.message || 'Error encountered when attempting to update group.',
          type: 'is-danger'
        });
        return;
      }
      // KyAnalytics client plugin, event tracker
      this.$kya.event('groups', 'updateGroup', 'Update Group clicked', { groupId: this.group.id });
      // reset the view so it's no longer editing
      this.resetEdit();
    },
    getAccountNameFromId(accountId) {
      if (!accountId || !this.allAccounts) return null;
      const accountMatch = this.allAccounts.find(a => a.id == accountId);
      return accountMatch ? accountMatch.name : 'Unknown';
    },
    sharedAccountSelected(accounts) {
      // should not be possible, but null out the email address value if accounts are selected
      if (accounts && !!accounts.length) this.groupEdit.inviteEmailAddress = null;
      this.groupEdit.sharedWith = accounts.map(a => ({ id: a.id, type: 'account' }));
      
      // check for edits
      this.assessChanges();
    },
    emailInputReceived(value) {
      if (!value) {
        // no email address provided, so we allow account selection
        this.canSelectAccount = true;
      } else {
        // an email address value is present, disable account selection
        this.canSelectAccount = false;
        this.groupEdit.sharedWith = [];
      }
      // check for edits
      this.assessChanges();
    },
  }
}
</script>

<style lang="scss" scoped>
  @import '../../styles/_colors';

  .kpt-detail-content-page {
    .kpt-email-share-wrapper {
      margin-left: 248.5px;
    }
    ::v-deep .kpt-group-entities-footer {
      left: 200px;
    }
  }
</style>
